import {
  ApplicationStep3,
  LegalRepresentation,
  PrivateSolicitor,
} from '../../../../../models/classes/ApplicationStep3.class';
import { SolicitorTypeEnum } from '../../../../../models/enum/solicitor-type.enum';

export type OwnerType = 'current' | 'proposed';

export type LegalDisplay =
  | { kind: 'hdb' }
  | { kind: 'private'; solicitor: PrivateSolicitor; showCheckbox?: boolean }
  | { kind: 'empty' }
  | { kind: 'none' };

export interface ResolvedLegalRepresentation {
  showLoanRow: boolean;
  loan: LegalDisplay;
  showConveyancingRow: boolean;
  conveyancing: LegalDisplay;
}

const HDB_DISPLAY: LegalDisplay = { kind: 'hdb' };
const NONE_DISPLAY: LegalDisplay = { kind: 'none' };

function resolvePrivate(
  solicitor: PrivateSolicitor,
  showCheckbox = false
): LegalDisplay {
  if (!solicitor.uen && !solicitor.name_of_firm) {
    return { kind: 'empty' };
  }
  return { kind: 'private', solicitor, showCheckbox };
}

function getOwnerContext(details: ApplicationStep3, ownerType: OwnerType) {
  const solicitorType =
    ownerType === 'current'
      ? details.current_solicitor_type
      : details.proposed_solicitor_type;
  const legalRepresentation: LegalRepresentation =
    ownerType === 'current' ? details.current : details.proposed;

  return { solicitorType, legalRepresentation };
}

export function resolveLegalRepresentation(
  details: ApplicationStep3,
  ownerType: OwnerType
): ResolvedLegalRepresentation {
  const { solicitorType, legalRepresentation } = getOwnerContext(details, ownerType);

  if (!solicitorType) {
    return {
      showLoanRow: false,
      loan: NONE_DISPLAY,
      showConveyancingRow: false,
      conveyancing: NONE_DISPLAY,
    };
  }

  switch (solicitorType) {
    case SolicitorTypeEnum.hdb_solicitor:
      return {
        showLoanRow: true,
        loan: HDB_DISPLAY,
        showConveyancingRow: true,
        conveyancing: legalRepresentation.hdb_solicitor.represented_same_solicitor
          ? HDB_DISPLAY
          : resolvePrivate(legalRepresentation.conveyancing_private_solicitor),
      };

    case SolicitorTypeEnum.private_solicitor:
      return {
        showLoanRow: true,
        loan: resolvePrivate(legalRepresentation.private_solicitor, true),
        showConveyancingRow: true,
        conveyancing: resolvePrivate(legalRepresentation.conveyancing_private_solicitor),
      };

    case SolicitorTypeEnum.private_mortgage_hdb_conveyancing_solicitor:
      return {
        showLoanRow: true,
        loan: resolvePrivate(legalRepresentation.private_solicitor),
        showConveyancingRow: true,
        conveyancing: HDB_DISPLAY,
      };

    case SolicitorTypeEnum.conveyancing_hdb_solicitor:
      return {
        showLoanRow: false,
        loan: NONE_DISPLAY,
        showConveyancingRow: true,
        conveyancing: HDB_DISPLAY,
      };

    case SolicitorTypeEnum.conveyancing_private_solicitor:
      return {
        showLoanRow: false,
        loan: NONE_DISPLAY,
        showConveyancingRow: true,
        conveyancing: resolvePrivate(legalRepresentation.conveyancing_private_solicitor),
      };

    default:
      return {
        showLoanRow: false,
        loan: NONE_DISPLAY,
        showConveyancingRow: false,
        conveyancing: NONE_DISPLAY,
      };
  }
}

export const LOAN_ROW_TITLES: Record<OwnerType, string> = {
  current: `Legal representation for current owners’ discharge of current loan`,
  proposed: `Legal representation for proposed owners' new mortgage loan`,
};

export const CONVEYANCING_ROW_TITLES: Record<OwnerType, string> = {
  current: `Legal representation for current owners’ conveyancing process`,
  proposed: `Legal representation for proposed owners' conveyancing process`,
};
